import { type ValueOf } from '@dni-types/utilities/value-of'

/**
 * @deprecated 舊版運送方法
 */
export interface Delivery {
  delivery_class: DeliveryClass
  delivery_name: string
  rule: number
  fee: number
}

export const DELIVERY_CLASS = {
  NORMAL: 'normal',
  FROZEN: 'frozen',
  SUBSHIP: 'subship',
  DONATION: 'donation',
} as const

export type DeliveryClass = ValueOf<typeof DELIVERY_CLASS>

export interface ShippingFee {
  id: number
  min_amount: number
  /**
   * 有可能無上限 ( null or undefined)
   */
  max_amount: number | null
  fee: number
  /**
   * 對應到 shipping_method_title
   *
   * excel: https://docs.google.com/spreadsheets/d/1nEeWe4kw52eJopMeliSN_A2QMN8d5aqk7vgbXg30xQE/edit?gid=1450986069#gid=1450986069
   */
  name: string
}

export interface ShippingRule {
  shipping_rule_id: number
  delivery_class: DeliveryClass
  name: string
  /**
   * 實際套用的運費，對照介面上顯示目前套用的部分
   */
  applied_shipping_fee: ShippingFee
  /**
   * 運費列表
   * ex: 0~99 收 80
   * 100~199 收 100
   */
  shipping_fees: ShippingFee[]
  /**
   * 排除的運送類別
   */
  excluded_shipping_class: string[]
}

export const SHIPPING_METHOD_TYPE = {
  // 原始運送方法 (Kelly ShippingMethod)
  PICKUP_711: 'pickup_711',
  PICKUP_FAMILY_MART: 'pickup_family_mart',
  PICKUP_FAMILY_MART_FROZEN: 'pickup_family_mart_frozen',
  PICKUP_FAMILY_MART_COMMUNITY: 'pickup_family_mart_community',
  PICKUP_HILIFE: 'pickup_hilife',
  PICKUP_OK_MART: 'pickup_ok_mart',
  STORE_TO_STORE_SHOPEE: 'store_to_store_shopee',
  HOME_DELIVERY_FROZEN: 'home_delivery_frozen',
  HOME_DELIVERY_NORMAL: 'home_delivery_normal',
  NORMAL_PALLET_SHIPMENT: 'normal_pallet_shipment',
  FROZEN_PALLET_SHIPMENT: 'frozen_pallet_shipment',
  HOME_DELIVERY_PROCESSED_FROZEN: 'home_delivery_processed_frozen',
  HOME_DELIVERY_PROCESSED_NORMAL: 'home_delivery_processed_normal',
  OVERSEAS_DELIVERY_HK: 'overseas_delivery_hk',
  OVERSEAS_DELIVERY_MO: 'overseas_delivery_mo',
  OVERSEAS_DELIVERY_SG: 'overseas_delivery_sg',
  OVERSEAS_DELIVERY_MY: 'overseas_delivery_my',
  OVERSEAS_DELIVERY_US: 'overseas_delivery_us',
  OVERSEAS_DELIVERY_KR: 'overseas_delivery_kr',
  PICKUP_OFFICE: 'pickup_office',
  DONATION_DELIVERY: 'donation_delivery',
  WOOSHIP: 'wooship',
  SHIPPING_PRESCO: 'shipping_presco',

  // 新增運送方法 (JH ShippingMethodTypeEnum)
  HOME_DELIVERY: 'home_delivery',
  CVS_711: 'cvs_711',
  CVS_FAMILY: 'cvs_family',
  CVS_HILIFE: 'cvs_hilife',
  CVS_OK: 'cvs_ok',
  SHOPEE: 'shopee',
  PALLET: 'pallet',

  SF_BUSINESS_POINT: 'sf_business_point',
  SF_LOCKER: 'sf_locker',
  SF_PARTNER_STORE: 'sf_partner_store',

  CVS_FAMILY_COMMUNITY: 'cvs_family_community',
} as const

export type ShippingMethodType = ValueOf<typeof SHIPPING_METHOD_TYPE>

export const ShippingMethodMap: Partial<Record<ShippingMethodType, string>> = {
  [SHIPPING_METHOD_TYPE.PICKUP_711]: '7-11超取',
  [SHIPPING_METHOD_TYPE.PICKUP_FAMILY_MART]: '全家超取',
  [SHIPPING_METHOD_TYPE.PICKUP_FAMILY_MART_FROZEN]: '全家冷凍超取',
  [SHIPPING_METHOD_TYPE.PICKUP_FAMILY_MART_COMMUNITY]: '全家智生活社區取',
  [SHIPPING_METHOD_TYPE.PICKUP_HILIFE]: '萊爾富超取',
  [SHIPPING_METHOD_TYPE.PICKUP_OK_MART]: 'OK超取',
  [SHIPPING_METHOD_TYPE.STORE_TO_STORE_SHOPEE]: '蝦皮店到店',
  [SHIPPING_METHOD_TYPE.HOME_DELIVERY_NORMAL]: '常溫宅配',
  [SHIPPING_METHOD_TYPE.HOME_DELIVERY_FROZEN]: '冷凍宅配',
  [SHIPPING_METHOD_TYPE.NORMAL_PALLET_SHIPMENT]: '常溫棧板出貨',
  [SHIPPING_METHOD_TYPE.FROZEN_PALLET_SHIPMENT]: '冷凍棧板出貨',
  [SHIPPING_METHOD_TYPE.HOME_DELIVERY_PROCESSED_FROZEN]: '加工冷凍宅配',
  [SHIPPING_METHOD_TYPE.HOME_DELIVERY_PROCESSED_NORMAL]: '加工常溫宅配',
  [SHIPPING_METHOD_TYPE.OVERSEAS_DELIVERY_HK]: '海外宅配-香港',
  [SHIPPING_METHOD_TYPE.OVERSEAS_DELIVERY_MO]: '海外宅配-澳門',
  [SHIPPING_METHOD_TYPE.OVERSEAS_DELIVERY_SG]: '海外宅配-新加坡',
  [SHIPPING_METHOD_TYPE.OVERSEAS_DELIVERY_MY]: '海外宅配-馬來西亞',
  [SHIPPING_METHOD_TYPE.OVERSEAS_DELIVERY_US]: '海外宅配-美國',
  [SHIPPING_METHOD_TYPE.OVERSEAS_DELIVERY_KR]: '海外宅配-韓國',
  [SHIPPING_METHOD_TYPE.PICKUP_OFFICE]: '辦公室取貨',
  [SHIPPING_METHOD_TYPE.DONATION_DELIVERY]: '公益宅配',
  [SHIPPING_METHOD_TYPE.WOOSHIP]: '台灣宅配',
  [SHIPPING_METHOD_TYPE.SHIPPING_PRESCO]: '數網出貨',
  [SHIPPING_METHOD_TYPE.SHOPEE]: '蝦皮店到店',
  [SHIPPING_METHOD_TYPE.HOME_DELIVERY]: '宅配',
  [SHIPPING_METHOD_TYPE.CVS_711]: '7-11超取',
  [SHIPPING_METHOD_TYPE.CVS_FAMILY]: '全家超取',
  [SHIPPING_METHOD_TYPE.CVS_HILIFE]: '萊爾富超取',
  [SHIPPING_METHOD_TYPE.CVS_OK]: 'OK超取',
  [SHIPPING_METHOD_TYPE.PALLET]: '棧板出貨',
  [SHIPPING_METHOD_TYPE.SF_BUSINESS_POINT]: '順豐營業點及順豐站',
  [SHIPPING_METHOD_TYPE.SF_LOCKER]: '順豐自助櫃',
  [SHIPPING_METHOD_TYPE.SF_PARTNER_STORE]: '順豐便利店',
  [SHIPPING_METHOD_TYPE.CVS_FAMILY_COMMUNITY]: '全家智生活社區取',
}

/**
 * 新版運送方法
 */
export interface ShippingMethod {
  shipping_method_id: number
  shipping_method_code: string
  shipping_method_type: ShippingMethodType
  name: string
  total_fee: number
  shipping_rules: Partial<Record<DeliveryClass, ShippingRule[]>>
}

export const SF_STORE_TYPE = {
  SF_BUSINESS_POINT: SHIPPING_METHOD_TYPE.SF_BUSINESS_POINT,
  SF_LOCKER: SHIPPING_METHOD_TYPE.SF_LOCKER,
  SF_PARTNER_STORE: SHIPPING_METHOD_TYPE.SF_PARTNER_STORE,
} as const

export type SfStoreType = ValueOf<typeof SF_STORE_TYPE>

export function isSfShippingType(shippingMethodType: string): shippingMethodType is SfStoreType {
  return (Object.values(SF_STORE_TYPE) as string[]).includes(shippingMethodType)
}

export const CVS_STORE_TYPE = {
  CVS_711: 'cvs_711',
  CVS_FAMILY: 'cvs_family',
} as const

export type CvsStoreType = ValueOf<typeof CVS_STORE_TYPE>

export const REYI_STORE_TYPE = {
  CVS_FAMILY_COMMUNITY: SHIPPING_METHOD_TYPE.CVS_FAMILY_COMMUNITY,
} as const

export type ReyiStoreType = ValueOf<typeof REYI_STORE_TYPE>

export function isReyiShippingType(shippingMethodType: string): shippingMethodType is ReyiStoreType {
  return (Object.values(REYI_STORE_TYPE) as string[]).includes(shippingMethodType)
}

export type CvsInfoStoreType = CvsStoreType | SfStoreType | ReyiStoreType

export function isCvsShippingType(shippingMethodType: string): shippingMethodType is CvsStoreType {
  return (Object.values(CVS_STORE_TYPE) as string[]).includes(shippingMethodType)
}

export function isPickupPointShipping(shippingMethodType: string): shippingMethodType is CvsInfoStoreType {
  return isCvsShippingType(shippingMethodType) || isSfShippingType(shippingMethodType) || isReyiShippingType(shippingMethodType)
}

export const REYI_MAP_STATUS = {
  SUCCESS: 'success',
  BACK: 'back',
  TIMEOUT: 'timeout',
  ERROR: 'error',
  REJECTED_STORE: 'rejected_store',
  INCOMPLETE: 'incomplete',
  EXPIRED: 'expired',
} as const

export type ReyiMapStatus = ValueOf<typeof REYI_MAP_STATUS>

export interface CvsStoreInfo {
  /**
   * 超商/自取點類型
   * cvs_711 / cvs_family（超商）、sf_business_point / sf_locker / sf_partner_store（順豐自取點）、cvs_family_community（全家智生活社區取）
   */
  store_type: CvsInfoStoreType
  /**
   * 超商店號
   */
  store_id: string
  /**
   * 超商店名
   */
  store_name: string
  /**
   * 超商地址
   * ex: 台北市大安區忠孝東路四段189號
   */
  store_address: string
  /** 7-11 / Family API 沒有區域資料時會回傳 null */
  region?: string | null
  district?: string | null
  community_name?: string | null
  /** 日翊比對後的收件地址，社區取以此為準，不用消費者原始輸入 */
  change_address?: string | null
  post?: string | null
  map_status?: ReyiMapStatus | null
  expires_at?: string | null
}

export interface SfPickupStore {
  service_code: string
  service_name: string
  service_address: string
  region: string
  district: string
}
